import { matchPath } from 'react-router';
import { detectBrandFromHostname, type SiteBrand } from '~/config/branding';
import {
  buildImpersonationHandoffHash,
  type ImpersonationHandoffState,
} from '~/features/user/impersonationHandoff';
import { buildCrossHostUrl } from './brandSwitch';
import { isProviderAllowedRoute } from './providerAllowedRoutes';
import { buildRegistrationPath, readRegistrationReturnTo } from './registrationReturnTo';
import { buildAuthCompletePath, isProviderAssociationPath } from './returnTo';
import { HLX_ROUTE_PATTERNS, VAULT_ROUTE_PATTERNS, paths } from './routes';

export type PostAuthRole =
  | 'loading'
  | 'provider'
  | 'employer'
  | 'impersonating'
  | 'choose-role'
  | 'registration-provider'
  | 'registration-employer'
  | 'unknown';

export type PostAuthDestination =
  | { type: 'loading' }
  | { type: 'local'; path: string }
  | { type: 'external'; url: string };

type ResolvePostAuthDestinationInput = {
  role: PostAuthRole;
  hostname: string;
  returnTo: string | null;
  impersonation?: ImpersonationHandoffState;
};

const getPathname = (path: string): string => {
  try {
    return new URL(path, 'https://app.local').pathname;
  } catch {
    return '';
  }
};

// [MCV-1521] A returnTo that only *looks* routable must not be preserved:
// these resolvers feed DefaultRoleRedirect, which mounts on the wildcard
// route. Preserving an unregistered path (a stale bookmark to a retired
// route, say /hlx/negotiations) makes <Navigate> target the same unmatched
// URL that just fell through — a same-path cycle that renders a permanently
// blank page. Match the pathname against the mounted route table with the
// router's own matcher (so params and wildcards behave exactly as routing
// does) and let anything unregistered fall back to the role dashboard.
// Matching only the pathname keeps search/hash intact on preserved paths.
const matchesRegisteredRoute = (patterns: readonly string[], path: string): boolean => {
  const pathname = getPathname(path);
  return patterns.some((pattern) => matchPath(pattern, pathname) !== null);
};

const isRegisteredHlxPath = (path: string | null): path is string =>
  Boolean(path && matchesRegisteredRoute(HLX_ROUTE_PATTERNS, path));

const isRegisteredVaultPath = (path: string | null): path is string =>
  Boolean(path && matchesRegisteredRoute(VAULT_ROUTE_PATTERNS, path));

const isProviderAllowedHlxPath = (path: string | null): path is string =>
  Boolean(path && isProviderAllowedRoute(getPathname(path)));

// [MCV-1588] Accepting a colleague invitation is the one returnTo that must
// outrank the role-based destination, because it is what ESTABLISHES the role:
// the accept endpoint creates the employer user and the EMPLOYER role. A
// freshly signed-up invitee resolves as `registration-employer`, and that
// branch otherwise returns the new-organization wizard unconditionally
// [MCV-1583] — which is not merely unnecessary for someone joining an existing
// organization, it is the wrong path entirely.
//
// EXACT patterns, never a prefix. `/employer-user-invitation/<anything-else>`
// is unmounted and falls through to the `*` wildcard where DefaultRoleRedirect
// lives, so preserving it by prefix would recreate exactly the same-path cycle
// MCV-1582 removed.
const INVITATION_ROUTE_PATTERNS: readonly string[] = [paths.employerUserInvitation.accept];

const isInvitationAcceptPath = (path: string | null): path is string =>
  Boolean(path && matchesRegisteredRoute(INVITATION_ROUTE_PATTERNS, path));

const normalizeReturnTo = (returnTo: string | null): string | null => {
  if (!returnTo) return null;

  try {
    const url = new URL(returnTo, 'https://app.local');
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

// The apex-swap/bare-apex-fallback/Clerk-sync rules live in `buildCrossHostUrl`
// (routing/brandSwitch.ts), shared with the generic "switch brand" affordance
// [MCV-1513]. This wrapper adds the one thing specific to post-login routing:
// funnel HLX targets through /auth/complete so role resolution runs again
// once the satellite session settles.
const toExternalUrl = (hostname: string, targetBrand: SiteBrand, targetPath: string): string => {
  const targetPathForHost = targetBrand === 'hlx' ? buildAuthCompletePath(targetPath) : targetPath;
  return buildCrossHostUrl(hostname, targetBrand, targetPathForHost);
};

const toDestination = (
  hostname: string,
  targetBrand: SiteBrand,
  targetPath: string
): PostAuthDestination => {
  const currentBrand = detectBrandFromHostname(hostname);

  if (!currentBrand || currentBrand === targetBrand) {
    return { type: 'local', path: targetPath };
  }

  return { type: 'external', url: toExternalUrl(hostname, targetBrand, targetPath) };
};

const resolveProviderPath = (returnTo: string | null): { brand: SiteBrand; path: string } => {
  if (isProviderAllowedHlxPath(returnTo)) {
    return { brand: 'hlx', path: returnTo };
  }

  if (isRegisteredVaultPath(returnTo)) {
    return { brand: 'mcv', path: returnTo };
  }

  return { brand: 'mcv', path: paths.vault.dashboard };
};

const resolveEmployerPath = (returnTo: string | null): { brand: SiteBrand; path: string } => ({
  brand: 'hlx',
  path: isRegisteredHlxPath(returnTo) ? returnTo : paths.hlx.dashboard,
});

const resolveImpersonationPath = (
  returnTo: string | null,
  impersonation?: ImpersonationHandoffState
): { brand: SiteBrand; path: string } => {
  const basePath = isRegisteredVaultPath(returnTo) ? returnTo : paths.vault.dashboard;
  const handoffHash = impersonation ? buildImpersonationHandoffHash(impersonation) : '';
  if (!handoffHash) return { brand: 'mcv', path: basePath };

  return { brand: 'mcv', path: `${basePath.split('#')[0]}${handoffHash}` };
};

export const resolvePostAuthDestination = ({
  role,
  hostname,
  returnTo,
  impersonation,
}: ResolvePostAuthDestinationInput): PostAuthDestination => {
  const requestedPath = normalizeReturnTo(returnTo);
  const normalizedReturnTo = requestedPath?.startsWith('/register/')
    ? readRegistrationReturnTo(new URL(requestedPath, 'https://app.local').search)
    : requestedPath;

  if (role === 'loading') return { type: 'loading' };

  // Checked ahead of every role branch — see INVITATION_ROUTE_PATTERNS. The
  // route is mounted cross-brand and outside the registration guards, so a
  // local navigation reaches it whatever state the user machine is in, and the
  // accept page itself explains the outcome (already a member elsewhere,
  // registered as a provider, expired) rather than failing silently.
  //
  // Impersonation is excluded deliberately: it is a support mode, and consuming
  // someone else's invitation while impersonating them is not a thing support
  // should be able to do by following a link.
  if (role !== 'impersonating' && isInvitationAcceptPath(normalizedReturnTo)) {
    return { type: 'local', path: normalizedReturnTo };
  }

  if (
    (role === 'provider' || role === 'employer') &&
    normalizedReturnTo &&
    isProviderAssociationPath(normalizedReturnTo)
  ) {
    return { type: 'local', path: normalizedReturnTo };
  }

  // Keep an invitation or app destination until registration completes. Unknown
  // paths are dropped so wildcard redirects cannot preserve an unroutable URL.
  const registrationReturnTo =
    normalizedReturnTo &&
    (isRegisteredHlxPath(normalizedReturnTo) ||
      isRegisteredVaultPath(normalizedReturnTo) ||
      isProviderAssociationPath(normalizedReturnTo))
      ? normalizedReturnTo
      : null;

  // [MCV-1583] Registration users get their wizard before the returnTo. They
  // have no role yet, so any app deep link they arrived with is somewhere we
  // cannot serve them; and this resolver feeds DefaultRoleRedirect, which
  // renders from the `*` wildcard. Resolving to nothing here (the old
  // `{type:'none'}`) rendered null on top of a URL that already matched no
  // route -- a blank page with no spinner and no way out but the URL bar.
  // Sending them to the wizard cannot loop: RegistrationRoute renders its
  // children while the machine is in a registration state and only redirects
  // when it is not.
  if (role === 'registration-provider') {
    return {
      type: 'local',
      path: buildRegistrationPath(paths.register.provider, registrationReturnTo),
    };
  }

  if (role === 'registration-employer') {
    return {
      type: 'local',
      path: buildRegistrationPath(paths.register.employer, registrationReturnTo),
    };
  }

  if (role === 'choose-role') {
    return {
      type: 'local',
      path: buildRegistrationPath(paths.register.roleSelect, registrationReturnTo),
    };
  }

  if (role === 'provider') {
    const target = resolveProviderPath(normalizedReturnTo);
    return toDestination(hostname, target.brand, target.path);
  }

  if (role === 'employer') {
    const target = resolveEmployerPath(normalizedReturnTo);
    return toDestination(hostname, target.brand, target.path);
  }

  if (role === 'impersonating') {
    const target = resolveImpersonationPath(normalizedReturnTo, impersonation);
    return toDestination(hostname, target.brand, target.path);
  }

  return { type: 'loading' };
};
